import React from 'react';
import { useForm } from 'react-hook-form';
import { PaymentButton } from './PaymentButton';
import { useCheckoutStore } from '../../store/checkoutStore';
import { useProductStore } from '../../store/productStore';
import { ArrowLeft } from 'lucide-react';
import { BumpOffer } from './BumpOffer';

interface Props {
  settings: {
    heading: string;
    buttonText: string;
    primaryColor: string;
    logo?: string;
  };
}

interface FormData {
  name: string;
  email: string;
  phone: string;
}

export const SimpleCardTemplate = ({ settings }: Props) => {
  const { currentPage, updatePageSettings } = useCheckoutStore();
  const getProduct = useProductStore((state) => state.getProduct);
  const { register, watch } = useForm<FormData>();
  
  const mainProduct = currentPage?.productId ? getProduct(currentPage.productId) : null;
  const bumpProduct = currentPage?.bumpOffer?.productId ? getProduct(currentPage.bumpOffer.productId) : null;
  const formData = watch();

  const handleBumpChange = (enabled: boolean) => {
    if (currentPage?.bumpOffer) {
      updatePageSettings({
        bumpOffer: { ...currentPage.bumpOffer, enabled }
      });
    }
  };

  if (!mainProduct || !currentPage) return null;

  const bumpAmount = currentPage.bumpOffer?.enabled && bumpProduct ? bumpProduct.price : 0;
  const totalAmount = mainProduct.price + bumpAmount;

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-sm p-8">
        <div className="flex items-center gap-2 mb-8">
          <button 
            onClick={() => window.history.back()}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-50"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          {settings.logo && (
            <img src={settings.logo} alt="Logo" className="h-8 ml-auto" />
          )}
        </div>

        <h1 className="text-2xl font-bold text-center mb-8">
          {settings.heading}
        </h1>

        <div className="space-y-6">
          {/* Product Info */}
          <div className="bg-gray-50 rounded-xl p-4">
            <h3 className="font-medium text-gray-900">{mainProduct.name}</h3>
            <div className="flex items-baseline gap-2 mt-1">
              <p className="text-2xl font-bold">₹{totalAmount}</p>
              {bumpAmount > 0 && (
                <p className="text-sm text-gray-500">
                  (Base: ₹{mainProduct.price} + Bump: ₹{bumpAmount})
                </p>
              )}
            </div>
            <p className="text-sm text-gray-500 mt-2">{mainProduct.description}</p>
          </div>

          {/* Bump Offer */}
          {currentPage.bumpOffer && bumpProduct && (
            <div className="border border-green-200 bg-green-50 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  checked={currentPage.bumpOffer.enabled}
                  onChange={(e) => handleBumpChange(e.target.checked)}
                  className="mt-1"
                />
                <div>
                  <h4 className="text-sm font-medium text-green-800">
                    Special Offer! Add {bumpProduct.name}
                  </h4>
                  <p className="text-sm text-green-700 mt-1">
                    {bumpProduct.description}
                  </p>
                  <p className="text-sm font-semibold text-green-800 mt-2">
                    Just ₹{bumpProduct.price} extra
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Contact Form */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Full Name
              </label>
              <input
                type="text"
                {...register('name', { required: true })}
                className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500 focus:ring-opacity-20 transition-colors"
                placeholder="John Doe"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email address
              </label>
              <input
                type="email"
                {...register('email', { required: true })}
                className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500 focus:ring-opacity-20 transition-colors"
                placeholder="you@example.com"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Phone number
              </label>
              <input
                type="tel"
                {...register('phone', { required: true })}
                className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500 focus:ring-opacity-20 transition-colors"
                placeholder="+91 98765 43210"
              />
            </div>
          </div>

          {/* Price Summary */}
          {bumpAmount > 0 && (
            <div className="space-y-2 border-t pt-4">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Base Price</span>
                <span>₹{mainProduct.price}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Order Bump</span>
                <span>₹{bumpAmount}</span>
              </div>
              <div className="flex justify-between text-base font-semibold pt-2 border-t">
                <span>Total</span>
                <span>₹{totalAmount}</span>
              </div>
            </div>
          )}

          <PaymentButton 
            amount={totalAmount}
            email={formData.email}
            name={formData.name}
            productName={mainProduct.name}
          />

          <p className="text-xs text-center text-gray-500">
            By continuing, you agree to our Terms of Service and Privacy Policy.
          </p>
        </div>
      </div>
    </div>
  );
};